import { AxiosResponse } from 'axios';
import axiosInstance from 'services/axios/axios';
import {
	BaseEsoDTO,
	JobCategoryDTO,
	JobCategoryMappingDTO,
	SearchResult,
	TrainingTrackDTO,
} from './_classes';
import { WrapImsPacket } from 'store/utility';
import { PERMISSIONS } from './_const';
import { then } from 'services/axios/_utility';

/**
 * @param {string} upn user principal name search term.
 * @returns {Promise<AxiosResponse<Object>>|Promise<AxiosResponse<null>>}
 */
export const getEMP = async upn => {
	try {
		return axiosInstance
			.get('api/Applications/Epic/Chronicles/User', {
				params: {
					upn: upn,
				},
			})
			.then(response => response?.data?.payload);
	} catch (ex) {
		throw ex;
	}
};

/**
 *
 * @param {number} id unique identifier of Job Category of interest.
 * @returns {Promise<AxiosResponse<JobCategoryDTO>}
 */
export const getJobCategory = async id => {
	return axiosInstance
		.get(`api/Utilities/EpicAdministration/JobCategories/${id}`)
		.then(response => {
			if (response.data?.payload) {
				return new JobCategoryDTO(response.data.payload);
			}
			return;
		});
};

/**
 *
 * @param {number} id unique identifier of Job Category of interest.
 * @returns {Promise<{mappingCount: number, userAssignmentCount: number}>}
 */
export const getJobCategoryDependencies = async id => {
	return axiosInstance
		.get(`api/Utilities/EpicAdministration/JobCategories/${id}/Dependencies`)
		.then(response => {
			if (response.data?.payload) {
				return {
					mappingCount:
						response.data.payload?.mappingCount ??
						response.data.payload?.MappingCount ??
						0,
					userAssignmentCount:
						response.data.payload?.userAssignmentCount ??
						response.data.payload?.UserAssignmentCount ??
						0,
				};
			}

			return { mappingCount: 0, userAssignmentCount: 0 };
		});
};

/**
 *
 * @param {string} input
 * @returns {Promise<AxiosResponse<SearchResult[]>>}
 */
export const searchJobCategories = async (
	input,
	sparse = false,
	archival = false,
) => {
	return axiosInstance
		.get('/api/Utilities/EpicAdministration/JobCategories/Search', {
			params: {
				search: input,
				sparse: sparse,
				archival: archival,
			},
		})
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new SearchResult(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {number} id unique identifier of Job Category of interest.
 * @returns {Promise<AxiosResponse<JobCategoryMappingDTO>}
 */
export const getJobCategoryMapping = async id => {
	return axiosInstance
		.get(`api/Utilities/EpicAdministration/JobCategoryMappings/${id}`)
		.then(response => {
			if (response.data?.payload) {
				return new JobCategoryMappingDTO(response.data.payload);
			}
			return;
		});
};

/**
 *
 * @param {string} input
 * @returns {Promise<AxiosResponse<JobCategoryMappingDTO[]>>}
 */
export const searchJobCategoryMappings = async (department, jobTitle) => {
	return axiosInstance
		.get('/api/Utilities/EpicAdministration/JobCategoryMappings/Search', {
			params: {
				department: department,
				jobTitle: jobTitle,
			},
		})
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new JobCategoryMappingDTO(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {number} id unique identifier of Job Category of interest.
 * @returns {Promise<AxiosResponse<JobCategoryMappingDTO>}
 */
export const getJobCategoryMappingsByJobCategoryID = async id => {
	return axiosInstance
		.get(
			`/api/Utilities/EpicAdministration/JobCategoryMappings/JobCategory/${id}`,
		)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new JobCategoryMappingDTO(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {number} id unique identifier of Job Category of interest.
 * @returns {Promise<AxiosResponse<JobCategoryMappingDTO>}
 */
export const getJobCategoryUsersByJobCategoryID = async id => {
	return axiosInstance
		.get(
			`/api/Utilities/EpicAdministration/JobCategoryMappings/JobCategory/${id}/Users`,
		)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new JobCategoryMappingDTO(el);
				});
			}
			return;
		});
};
/**
 *
 * @param {*} id unique identifier of training track of interest.
 * @returns {Promise<AxiosResponse<JobCategoryDTO[]>}
 */
export const getJobCategoriesByTrainingTrack = async id => {
	return axiosInstance
		.get(
			`api/Utilities/EpicAdministration/TrainingTracks/${id}/JobCategories`,
		)
		.then(response => {
			if (response.data?.payload && response.data.payload?.length > 0) {
				return response.data.payload.map(jc => new JobCategoryDTO(jc));
			}
			return [];
		});
};

/**
 *
 * @param {number} id unique identifier of training track of interest.
 * @returns {Promise<AxiosResponse<TrainingTrackDTO>}
 */
export const getTrainingTrack = async id => {
	return axiosInstance
		.get(`api/Utilities/EpicAdministration/TrainingTracks/${id}`)
		.then(response => {
			if (response.data?.payload) {
				return new TrainingTrackDTO(response.data.payload);
			}
			return;
		});
};

/**
 *
 * @param {string} input
 * @returns {Promise<AxiosResponse<SearchResult[]>>}
 */
export const searchTrainingTracks = async (input, sparse = false) => {
	return axiosInstance
		.get('/api/Utilities/EpicAdministration/TrainingTracks/Search', {
			params: {
				search: input,
				sparse: sparse,
			},
		})
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new SearchResult(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {string} input
 * @returns {Promise<AxiosResponse<SearchResult[]>>}
 */
export const searchJobCategoryMappingDepartments = async (
	input,
	sparse = false,
) => {
	return axiosInstance
		.get(
			'/api/Utilities/EpicAdministration/JobCategoryMappings/Departments/Search',
			{
				params: {
					search: input,
					sparse: sparse,
				},
			},
		)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new SearchResult(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {string} input
 * @returns {Promise<AxiosResponse<SearchResult[]>>}
 */
export const searchJobCategoryMappingJobTitles = async (
	input,
	sparse = false,
) => {
	return axiosInstance
		.get(
			'/api/Utilities/EpicAdministration/JobCategoryMappings/JobTitles/Search',
			{
				params: {
					search: input,
					sparse: sparse,
				},
			},
		)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new SearchResult(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {string} input
 * @returns {Promise<AxiosResponse<SearchResult[]>>}
 */
export const searchHRDepartments = async input => {
	return axiosInstance
		.get(
			'/api/Utilities/EpicAdministration/JobCategoryMappings/HRDepartments/Search',
			{
				params: {
					search: input,
				},
			},
		)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new SearchResult(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {string} input
 * @returns {Promise<AxiosResponse<SearchResult[]>>}
 */
export const searchHRJobTitles = async input => {
	return axiosInstance
		.get(
			'/api/Utilities/EpicAdministration/JobCategoryMappings/HRJobTitles/Search',
			{
				params: {
					search: input,
				},
			},
		)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new SearchResult(el);
				});
			}
			return;
		});
};

export const submitJobCategoryForm = async (values, isNew) => {
	if (isNew) {
		return axiosInstance
			.post(
				'/api/Utilities/EpicAdministration/JobCategories/',
				WrapImsPacket(values),
			)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	} else {
		return axiosInstance
			.put(
				'/api/Utilities/EpicAdministration/JobCategories/',
				WrapImsPacket(values),
			)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	}
};

export const submitJobCategoryMappingForm = async (values, isNew) => {
	if (isNew) {
		return axiosInstance
			.post(
				'/api/Utilities/EpicAdministration/JobCategoryMappings/',
				WrapImsPacket(values),
			)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	} else {
		return axiosInstance
			.put(
				'/api/Utilities/EpicAdministration/JobCategoryMappings/',
				WrapImsPacket(values),
			)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	}
};

export const submitTrainingTrackForm = async (values, isNew) => {
	if (isNew) {
		return axiosInstance
			.post(
				'/api/Utilities/EpicAdministration/TrainingTracks/',
				WrapImsPacket(values),
			)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	} else {
		return axiosInstance
			.put(
				'/api/Utilities/EpicAdministration/TrainingTracks/',
				WrapImsPacket(values),
			)
			.then(response => {
				return response;
			})
			.catch(error => {
				throw error;
			});
	}
};

/**
 * @returns {Promise<AxiosResponse<void>>}
 */
export const getBulkUploadTemplate = async () => {
	return axiosInstance
		.get('/api/Components/UserUpload/Template/Bridges')
		.then(response => response)
		.catch(error => {
			throw error;
		});
};

/**
 * @param {number} id unique identifier of user of interest.
 * @param {string[]} contents array of CSV rows, header row inclusive.
 * @param {Object} dispatch nullable redux object for raising banners.
 * @returns {Promise<AxiosResponse<Object[]>>} array of CSV rows that could not be processed.
 */
export const postBulkUpload = async (id, contents, dispatch = null) => {
	return axiosInstance
		.post(
			`/api/Utilities/EpicAdministration/BulkUpload/${id}`,
			WrapImsPacket(contents),
		)
		.then(response => {
			return then(response, payload => payload, dispatch);
		})
		.catch(error => {
			throw error.response?.data?.payload ?? 'Error occurred.';
		});
};

/**
 *
 * @param {number} id unique identifier of user of interest.
 * @param {*} appID unique identifier of application of interest.
 * @returns {Promise<AxiosResponse<Object>} user's nullable application or application request information.
 */
export const getUserAppStatus = async (id, appID) => {
	return axiosInstance
		.get(`api/personnel/${id}/applications/${appID}`)
		.then(response => response.data?.payload ?? null);
};

/**
 *
 * @param {number} upn user principal name of user of interest.
 * @param {string} groupID object ID of Azure group of interest.
 * @param {boolean} useDisplayName whether to repurpose group ID as group display name.
 * @returns {Promise<AxiosResponse<boolean>>}
 */
export const getUserAzureGroup = async (
	upn,
	groupID,
	useDisplayName = false,
) => {
	return axiosInstance
		.get(`api/AzureAD/${upn}/CheckGroupMembership/${groupID}`, {
			params: { useDisplayName: useDisplayName },
		})
		.then(response =>
			response.data?.payload !== null ? response.data.payload : false,
		)
		.catch(ex => {
			throw ex;
		});
};

/**
 *
 * @param {number} id unique identifier of training track of interest.
 * @returns {Promise<AxiosResponse<BaseEsoDTO>}
 */
export const getUserWithRole = async () => {
	return axiosInstance
		.get(`api/Utilities/Security/Roles/${PERMISSIONS.BRIDGES_ROLE}/Users`)
		.then(response => {
			if (response.data?.payload) {
				return response.data.payload.map(el => {
					return new BaseEsoDTO(el);
				});
			}
			return;
		});
};

/**
 *
 * @param {import('context/User/UserContext').User} user logged-in user.
 * @param {number} reportID unique identifier of report of interest still requiring validation.
 * @returns {Promise<AxiosResponse<boolean>>}
 */
export const postReminderEmail = async (user, reportID) => {
	return axiosInstance
		.post('api/ManagerValidation/TriggerValidationEmail', {
			id: reportID,
			createUser: user,
			maintenanceUser: user,
		})
		.then(response => response.data.payload);
};

/**
 *
 * @param {import('context/User/UserContext').User} user logged-in user.
 * @param {number} reportID unique identifier of report whose training state is being flipped.
 * @param {number} jobCategoryID unique identifier of job category whose training state is being flipped.
 * @param {boolean} trained new training state.
 * @returns {Promise<AxiosResponse<boolean>>}
 */
export const postTrainingFlip = async (
	user,
	reportID,
	jobCategoryID,
	trained,
) => {
	return axiosInstance
		.post('api/ManagerValidation/ToggleTrainingCompletion', {
			id: reportID,
			createUser: user,
			maintenanceUser: user,
			jobCategoryID: jobCategoryID,
			value: trained,
		})
		.then(response => response);
};

/**
 *
 * @param {BaseEsoDTO} user unique identifier of user to provide a new role
 * @returns {Promise<AxiosResponse<boolean>}
 */
export const removeRoleFromUser = async user => {
	return axiosInstance
		.delete(
			`api/Utilities/Security/Roles/${PERMISSIONS.BRIDGES_ROLE}/Users/${user.ID}`,
		)
		.then(response => {
			return response;
		})
		.catch(error => {
			throw error;
		});
};

/**
 *
 * @param {BaseEsoDTO} user unique identifier of user to provide a new role
 * @returns {Promise<AxiosResponse<boolean>}
 */
export const addRoleToUser = async user => {
	return axiosInstance
		.post(
			`api/Utilities/Security/Roles/${PERMISSIONS.BRIDGES_ROLE}/Users/${user.ID}`,
		)
		.then(response => {
			return response;
		})
		.catch(error => {
			throw error;
		});
};
